// ============================================================
// TMR Portal Admin — como a carteira de taxas se lê na tela
// ============================================================
import type { RatesOutOfSyncItem, RatesOutOfSyncPage, ReconcilePageResult, ReconcileRun } from './contract';

/**
 * Why the list is empty. Three different sentences, and the whole reason this function exists: the same
 * empty array arrives for "everything agrees", "we could not ask the legacy" and "you may see nothing", and
 * a screen about money must not show one when it means another.
 */
export type EmptyReason = 'in-sync' | 'legacy-unavailable' | 'out-of-scope' | 'not-empty';

/**
 * Which of the three empty answers this page is, or `not-empty` when there is something to show.
 *
 * Order matters. Out of scope is checked first because it makes every other count meaningless — nothing was
 * compared, so "the legacy answered" says nothing about agreement.
 *
 * @param page The page as the server sent it.
 */
export function emptyReason(page: RatesOutOfSyncPage): EmptyReason {
  if (page.items.length > 0) return 'not-empty';
  if (page.outOfScope) return 'out-of-scope';
  if (!page.legacyAvailable) return 'legacy-unavailable';
  return 'in-sync';
}

/** What each empty answer says on screen. */
export const EMPTY_MESSAGE: Record<Exclude<EmptyReason, 'not-empty'>, string> = {
  'in-sync': 'Nenhuma empresa está fora de sincronia com o sistema legado.',
  'legacy-unavailable':
    'Não foi possível ler o sistema legado agora, então não dá para afirmar que os valores conferem.',
  'out-of-scope': 'Você não tem alcance sobre nenhuma empresa, então não há o que comparar.',
};

/** The hint under each empty answer — what to do about it, when there is anything to do. */
export const EMPTY_HINT: Record<Exclude<EmptyReason, 'not-empty'>, string | undefined> = {
  'in-sync': undefined,
  'legacy-unavailable': 'Tente de novo em alguns instantes.',
  'out-of-scope': 'Peça o alcance a quem administra os papéis.',
};

/**
 * Whether this Empresa is actually out of step with the legacy, as opposed to merely waiting for us.
 *
 * `awaitingExport` alone is our own outbox still working — the rate was written here seconds ago and the
 * worker has not carried it over yet. It is listed, because an export that never completes is a real
 * problem, but it is not the same claim as "the legacy holds another number".
 *
 * @param item One Empresa's comparison.
 */
export function isDiverging(item: RatesOutOfSyncItem): boolean {
  return item.drifted > 0 || item.missing > 0;
}

/**
 * How one Empresa's disagreement reads, in the order that matters: what the legacy is charging differently
 * first, the broken correlations next, and our own pending work last.
 *
 * @param item One Empresa's comparison.
 */
export function disagreementSummary(item: RatesOutOfSyncItem): string {
  const parts: string[] = [];

  if (item.drifted > 0) {
    parts.push(item.drifted === 1
      ? '1 taxa com valor diferente no legado'
      : `${item.drifted} taxas com valor diferente no legado`);
  }

  if (item.missing > 0) {
    parts.push(item.missing === 1
      ? '1 taxa sem a linha correspondente lá'
      : `${item.missing} taxas sem a linha correspondente lá`);
  }

  if (item.awaitingExport > 0) {
    parts.push(item.awaitingExport === 1
      ? '1 aguardando exportação'
      : `${item.awaitingExport} aguardando exportação`);
  }

  return parts.join(' · ');
}

/** A blank accumulator for a walk of the portfolio. */
export function emptyRun(): ReconcileRun {
  return { companies: 0, created: 0, updated: 0, removed: 0, failed: 0, pages: 0, completed: false };
}

/**
 * Folds one page's result into the running total.
 *
 * Accumulated on the screen rather than on the server because the walk belongs to the screen: it is the
 * operator who decides to carry on to the next page or to stop, and a total the server kept would be a
 * total for a walk it does not control.
 *
 * @param run The total so far.
 * @param page What the page just done reported.
 */
export function accumulate(run: ReconcileRun, page: ReconcilePageResult): ReconcileRun {
  return {
    companies: run.companies + page.companies,
    created: run.created + page.created,
    updated: run.updated + page.updated,
    removed: run.removed + page.removed,
    failed: run.failed + page.failed,
    pages: run.pages + 1,
    completed: page.lastCompanyId === null,
  };
}

/**
 * What a walk of the portfolio did, as one sentence.
 *
 * It says "nada mudou" out loud rather than showing an unchanged screen: a reconciliation that found
 * nothing to bring and one that did not run look identical otherwise — the lesson the Empresa tab's own
 * sync summary was written for.
 *
 * @param run The accumulated totals.
 */
export function runSummary(run: ReconcileRun): string {
  const companies = run.companies === 1 ? '1 empresa' : `${run.companies} empresas`;
  const changes: string[] = [];

  if (run.created > 0) changes.push(`${run.created} trazida${run.created === 1 ? '' : 's'}`);
  if (run.updated > 0) changes.push(`${run.updated} atualizada${run.updated === 1 ? '' : 's'}`);
  if (run.removed > 0) changes.push(`${run.removed} removida${run.removed === 1 ? '' : 's'}`);

  const head = run.completed
    ? `Carteira reconciliada: ${companies}`
    : `Reconciliação interrompida: ${companies}`;

  const tail = changes.length === 0 ? ', nada mudou' : `, ${changes.join(', ')}`;
  const failed = run.failed === 0
    ? ''
    : ` ${run.failed} empresa${run.failed === 1 ? '' : 's'} não pôde ser reconciliada — normalmente por ainda não ter sido replicada para o legado.`;

  return `${head}${tail}.${failed}`;
}
